import React, { useState } from "react";

// ─── Design tokens (consistent with app's dark palette) ───────────────────────
// bg: black / gray-900     surface: gray-800 / slate-800
// accent: cyan-300/400     warm: yellow-200/300
// text: gray-100 / white   muted: gray-400
// border: gray-700 / cyan-700
// ─────────────────────────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  {
    step: "01",
    who: "Parent observes at home",
    what:
      "You answer structured questions about your child's attention, routines, emotions, and learning habits — based on what you actually see day to day.",
    icon: "🏠",
  },
  {
    step: "02",
    who: "Teacher & doctor observe independently",
    what:
      "The same child is observed by their educator at school and, optionally, by a doctor — each through their own lens, without seeing each other's responses.",
    icon: "🏫",
  },
  {
    step: "03",
    who: "Patterns emerge across settings",
    what:
      "GrowthLoop surfaces where observations agree or diverge. A pattern seen at home, school, and clinic carries more weight than one seen in a single setting.",
    icon: "📊",
  },
  {
    step: "04",
    who: "You act early — with context",
    what:
      "Instead of waiting for failure to accumulate, you have a shared language and structured evidence to support your child's development before challenges become entrenched.",
    icon: "🎯",
  },
];

const FOR_WHOM = [
  {
    role: "Parents",
    icon: "🏠",
    description:
      "You notice something is off but can't name it. GrowthLoop helps you observe systematically and communicate what you see to teachers and doctors — without guesswork.",
  },
  {
    role: "Educators",
    icon: "📚",
    description:
      "You see 30 children a day. GrowthLoop gives you a structured way to note developmental patterns and share them with families and clinicians in a common format.",
  },
  {
    role: "Doctors & Counsellors",
    icon: "🩺",
    description:
      "You need cross-setting data, not just a 20-minute consultation snapshot. GrowthLoop brings home and school observations into your clinical picture.",
  },
];

const WHAT_IT_DOES = [
  {
    label: "Externalises your child's executive function",
    plain:
      "Breaks tasks into small steps, tracks consistency, and makes invisible habits visible.",
  },
  {
    label: "Shows patterns across home, school, and clinic",
    plain:
      "Connects observations from three different adults who each see a different side of your child.",
  },
  {
    label: "Shifts focus from outcomes to effort",
    plain:
      "Tracks what your child tried, not just what they got right — so progress is visible even before grades improve.",
  },
  {
    label: "Supports emotional regulation",
    plain:
      "Guides parents and children through structured reflection rather than reaction.",
  },
  {
    label: "Replaces blame with process",
    plain:
      "Reframes 'my child won't focus' as 'what in the routine is making focus harder?' — a question with answers.",
  },
];

const WHAT_IT_IS_NOT = [
  "Not a medical or psychological diagnostic tool",
  "Not a replacement for professional evaluation or therapy",
  "Not a behaviour-control or punishment system",
  "Not limited to children with ADHD diagnoses",
  "Not a reporting tool that labels your child",
];

const FRAMEWORK_TOOLS = [
  {
    name: "Chronicle",
    desc: "Tracks effort, attempts, and continuity over time — not just outcomes.",
  },
  {
    name: "Root Cause Analysis",
    desc: "Helps children and parents understand why a pattern exists, without blame.",
  },
  {
    name: "Applause",
    desc: "Reinforces effort and persistence — not comparison to others.",
  },
  {
    name: "Behavioural Diary",
    desc: "Builds emotional vocabulary and structured self-reflection over time.",
  },
  {
    name: "Micro Lean Loops",
    desc: "Converts overwhelming challenges into small, repeatable routines.",
  },
];

const CONCERNS = {
  student: {
    label: "Child / Student",
    icon: "🎯",
    items: [
      "Reducing academic pressure",
      "Time and task management",
      "Screen & technology overuse",
      "Peer pressure",
      "Homework management",
      "Behavioural patterns",
      "Emotional well-being",
      "Health and nutrition habits",
      "Sleep routine",
      "Communication skills",
      "Financial literacy basics",
    ],
  },
  family: {
    label: "Family",
    icon: "🏠",
    items: [
      "Safety and security awareness",
      "Independence and responsibility",
      "Cultural identity",
      "Career guidance readiness",
      "Language skills",
      "Academic support at home",
      "Bullying",
      "Substance abuse awareness",
    ],
  },
  society: {
    label: "Society",
    icon: "🌍",
    items: [
      "Cultural and social integration",
      "Respect for elders",
      "Responsible social media use",
      "Community involvement",
      "Family bonding time",
    ],
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="h-px flex-1 bg-cyan-900" />
      <span className="text-xs uppercase tracking-widest text-cyan-500 font-semibold">
        {children}
      </span>
      <div className="h-px flex-1 bg-cyan-900" />
    </div>
  );
}

function ConcernsTabs() {
  const [active, setActive] = useState("student");
  const current = CONCERNS[active];

  return (
    <div>
      <div className="flex gap-1 mb-3">
        {Object.entries(CONCERNS).map(([key, val]) => (
          <button
            key={key}
            onClick={() => setActive(key)}
            className={`flex-1 py-1.5 text-xs rounded transition ${
              active === key
                ? "bg-cyan-500 text-black font-semibold"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            {val.icon} {val.label}
          </button>
        ))}
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {current.items.map((item, i) => (
          <li
            key={i}
            className="text-xs text-gray-200 bg-gray-800 rounded px-3 py-2 flex items-start gap-2"
          >
            <span className="text-cyan-500 mt-0.5">›</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function GrowthLoopAbout() {
  return (
    <div className="max-w-3xl mx-auto space-y-10 text-gray-100 pb-8">

      {/* ── Hero ── */}
      <div className="text-center space-y-3 pt-2">
        <div className="inline-block bg-cyan-950 border border-cyan-700 rounded-full px-3 py-1 text-xs uppercase tracking-widest text-cyan-400 font-semibold">
          Non-clinical · Preventive · Pattern-first
        </div>
        <h1 className="text-2xl lg:text-3xl font-bold text-white leading-tight">
          Observe your child's development<br />
          <span className="text-cyan-300">before challenges become labels.</span>
        </h1>
        <p className="text-sm text-gray-300 max-w-xl mx-auto leading-relaxed">
          GrowthLoop helps parents, educators, and doctors observe the same
          child across three settings — and surface patterns early, together.
          No diagnosis. No blame. Just structured visibility.
        </p>
      </div>

      {/* ── How it works ── */}
      <div>
        <SectionLabel>How it works</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {HOW_IT_WORKS.map((step) => (
            <div
              key={step.step}
              className="bg-gray-900 border border-gray-700 rounded-xl p-4 flex gap-3"
            >
              <div className="flex-shrink-0 text-xl">{step.icon}</div>
              <div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-mono text-cyan-500">
                    {step.step}
                  </span>
                  <span className="text-xs font-semibold text-cyan-200">
                    {step.who}
                  </span>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  {step.what}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Who it's for ── */}
      <div>
        <SectionLabel>Who it's for</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {FOR_WHOM.map((item) => (
            <div
              key={item.role}
              className="bg-gray-900 border border-gray-700 rounded-xl p-4 space-y-2"
            >
              <div className="text-2xl">{item.icon}</div>
              <div className="text-sm font-semibold text-cyan-200">
                {item.role}
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Child challenges sub-section */}
        <div className="mt-4 bg-gray-900 border border-gray-700 rounded-xl p-4">
          <p className="text-xs text-gray-400 mb-3">
            GrowthLoop covers a wide range of concerns parents and educators
            actually face:
          </p>
          <ConcernsTabs />
        </div>
      </div>

      {/* ── What it does ── */}
      <div>
        <SectionLabel>What GrowthLoop does</SectionLabel>
        <div className="space-y-2">
          {WHAT_IT_DOES.map((item, i) => (
            <div
              key={i}
              className="bg-gray-900 border border-gray-700 rounded-xl p-4"
            >
              <div className="text-xs font-semibold text-cyan-200 mb-1">
                {item.label}
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                {item.plain}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Think5Whys framework ── */}
      <div>
        <SectionLabel>Powered by Think5Whys</SectionLabel>
        <p className="text-xs text-gray-400 mb-3 leading-relaxed">
          GrowthLoop is built on the Think5Whys transformation framework —
          adapting principles from behavioural science, executive function
          research, and process improvement into child-friendly, parent-guided
          tools.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {FRAMEWORK_TOOLS.map((tool) => (
            <div
              key={tool.name}
              className="bg-gray-800 border border-gray-700 rounded-lg p-3"
            >
              <div className="text-xs font-semibold text-yellow-300 mb-1">
                {tool.name}
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                {tool.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── What it is not ── */}
      <div>
        <SectionLabel>What GrowthLoop is not</SectionLabel>
        <ul className="space-y-2">
          {WHAT_IT_IS_NOT.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-xs text-gray-300 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5"
            >
              <span className="text-red-400 font-bold mt-0.5">✕</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* ── Closing statement ── */}
      <div className="border border-cyan-800 bg-cyan-950 rounded-xl p-5 text-center space-y-2">
        <p className="text-sm text-cyan-200 font-medium leading-relaxed">
          GrowthLoop works alongside parents, schools, and clinicians —
          creating a shared language and structured support system that helps
          children build confidence, capability, and control, step by step.
        </p>
        <p className="text-xs text-cyan-400 italic">
          Growth, not labels · Patterns, not punishment · Systems, not blame
        </p>
      </div>

    </div>
  );
}
